import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Button, Modal, Form, Spinner, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import { FaDownload, FaTrashAlt, FaUpload, FaEye, FaCalendarAlt, FaThermometerHalf, FaHistory, FaThermometerFull, FaThermometerEmpty, FaClock, FaBan, FaInfoCircle } from "react-icons/fa";
import { HiAdjustments } from "react-icons/hi";
import { BiSearch } from "react-icons/bi";
import { IoClose } from "react-icons/io5";

import { baseDatosService } from '../../services/baseDatosService';
import { getProyectosEmpresa } from '../../services/proyectoService';
import { vendedorService } from '../../services/vendedorService';
import axios from '../../auth/authInterceptor';
import ConfirmacionModal from '../../components/form-components/confirmacion-modal';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./base-datos.scss";

registerLocale("es", es);

const STATUS_COLORS = {
  pendiente: { bg: '#F3F4F6', text: '#6B7280' },
  alto: { bg: '#FEE2E2', text: '#991B1B' },
  medio: { bg: '#FEF3C7', text: '#92400E' },
  bajo: { bg: '#DBEAFE', text: '#1E40AF' },
  descartado: { bg: '#E5E7EB', text: '#374151' },
  default: { bg: '#F3F4F6', text: '#6B7280' }
};

const CLASIFICACION_OPTIONS = [
  { value: "pendiente", label: "Pendiente", Icon: FaClock },
  { value: "alto", label: "Alto (Caliente)", Icon: FaThermometerFull },
  { value: "medio", label: "Medio (Tibio)", Icon: FaThermometerHalf },
  { value: "bajo", label: "Bajo (Frío)", Icon: FaThermometerEmpty },
  { value: "descartado", label: "Descartado", Icon: FaBan },
];

const PLANTILLA_IMPORTACION_NOMBRE = 'plantilla_importacion.xlsx';
const ALERTA_PROYECTO_IMPORTACION = 'El archivo contiene un ID de proyecto no válido. Revise la columna project_id y use solo los proyectos habilitados que se muestran en la advertencia.';
const ALERTA_ASESOR_IMPORTACION = 'El archivo contiene un ID de asesor no válido. Revise la columna asesor_id y use solo los asesores registrados de esta empresa.';

const ClasificacionBadge = ({ clasificacion }) => {
  const option = CLASIFICACION_OPTIONS.find(o => o.value === clasificacion) || CLASIFICACION_OPTIONS[0];
  const IconComponent = option.Icon;
  const colors = STATUS_COLORS[clasificacion] || STATUS_COLORS.default;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 500,
        backgroundColor: colors.bg,
        color: colors.text,
        width: 'fit-content'
      }}
    >
      <IconComponent size={14} />
      <span>{option.label.split(' ')[0]}</span>
    </Box>
  );
};

// Componente Modal de Detalles del Lead
const DetalleLeadModal = ({ show, onHide, leadUuid, leadNombre }) => {
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && leadUuid) {
      loadDetalle();
    }
  }, [show, leadUuid]);

  const loadDetalle = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await baseDatosService.getDetalleLeadClasificacion(leadUuid);
      if (response.success) {
        setDetalle(response.data);
      }
    } catch (err) {
      console.error(err);
      setError('Error al cargar los detalles del lead');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';

    if (typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [year, month, day] = fecha.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-PE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }

    return new Date(fecha).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatHora = (hora) => {
    if (!hora) return '';
    return hora.substring(0, 5);
  };

  const formatResumenConversacion = (contenido) => {
    if (!contenido) return '';

    const seen = new Set();

    return contenido
      .split('\n')
      .map((linea) => linea.trim())
      .filter(Boolean)
      .map((linea) => {
        const tieneBullet = linea.startsWith('•');
        const sinBullet = linea.replace(/^•\s*/, '');
        const limpio = sinBullet.replace(/^Paso\s+\d+\s*-\s*/i, '').trim();
        return tieneBullet ? `• ${limpio}` : limpio;
      })
      .filter((linea) => {
        const key = linea
          .replace(/^•\s*/, '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();

        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .join('\n');
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaEye />
          <span>Detalle del Lead: {leadNombre || 'Sin nombre'}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Cargando información...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : detalle ? (
          <div className="detalle-lead-content">
            {/* Información del Lead */}
            <div className="card mb-3 border-0 bg-light">
              <div className="card-body">
                <h6 className="card-title text-primary mb-3">
                  👤 Información del Contacto
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Nombre:</strong> {detalle.lead.nombre} {detalle.lead.apellido}</p>
                    <p className="mb-1"><strong>Teléfono:</strong> {detalle.lead.telefono}</p>
                    <p className="mb-1"><strong>Email:</strong> {detalle.lead.email || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <p className="mb-1"><strong>DNI:</strong> {detalle.lead.dni || 'N/A'}</p>
                    <p className="mb-1"><strong>Registro:</strong> {formatFecha(detalle.lead.fechaRegistro)}</p>
                    <p className="mb-1">
                      <strong>Estado Actual:</strong>{' '}
                      <ClasificacionBadge clasificacion={detalle.estadoActual} />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de Conversación (IA) */}
            {detalle.resumen.contenido && (
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-body bg-white rounded">
                  <h6 className="card-title text-primary mb-3 d-flex align-items-center gap-2">
                    <span></span> Resumen de Conversación
                  </h6>
                  <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0d6efd' }}>
                    <div style={{ whiteSpace: 'pre-line', color: '#495057', lineHeight: '1.6' }}>
                      {formatResumenConversacion(detalle.resumen.contenido)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Resumen Rápido */}
            <div className="row mb-3">
              <div className="col-4">
                <div className="card text-center border-0" style={{ backgroundColor: '#eef2ff' }}>
                  <div className="card-body py-2">
                    <h3 className="mb-0 fw-bold" style={{ color: '#273c75' }}>{detalle.resumen.totalClasificaciones}</h3>
                    <small className="text-muted fw-semibold">Clasificaciones</small>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="card text-center border-0" style={{ backgroundColor: '#e0f2f1' }}>
                  <div className="card-body py-2">
                    <h3 className="mb-0 fw-bold" style={{ color: '#00695c' }}>{detalle.prospectos.length}</h3>
                    <small className="text-muted fw-semibold">Prospectos</small>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="card text-center border-0" style={{ backgroundColor: '#f0fdf4' }}>
                  <div className="card-body py-2">
                    <h3 className="mb-0 fw-bold" style={{ color: '#15803d' }}>{detalle.resumen.totalCitas}</h3>
                    <small className="text-muted fw-semibold">Citas Agendadas</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Historial de Clasificaciones */}
            <div className="card mb-3 border-0 shadow-sm">
              <div className="card-header bg-white border-bottom-0">
                <h6 className="mb-0 d-flex align-items-center gap-2">
                  <FaThermometerHalf className="text-danger" />
                  Historial de Clasificaciones
                </h6>
              </div>
              <div className="card-body p-0">
                {detalle.clasificaciones.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <FaHistory size={32} className="mb-2 opacity-50" />
                    <p className="mb-0">Sin clasificaciones aún</p>
                    <small>El bot clasificará este lead durante la conversación</small>
                  </div>
                ) : (
                  <div className="timeline">
                    {detalle.clasificaciones.map((c, idx) => (
                      <div key={c.id}
                        className={`timeline-item ${idx === 0 ? 'timeline-item-current' : ''}`}
                        style={{
                          padding: '12px 16px',
                          borderLeft: `3px solid ${idx === 0 ? '#dc3545' : '#dee2e6'}`,
                          marginLeft: '8px',
                          backgroundColor: idx === 0 ? 'rgba(220, 53, 69, 0.05)' : 'transparent'
                        }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <ClasificacionBadge clasificacion={c.clasificacion} />
                            {c.razon && (
                              <p className="mt-2 mb-0 text-muted small">
                                <em>"{c.razon}"</em>
                              </p>
                            )}
                          </div>
                          <small className="text-muted">
                            {formatFecha(c.fecha)}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Historial de Citas */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom-0">
                <h6 className="mb-0 d-flex align-items-center gap-2">
                  <FaCalendarAlt className="text-primary" />
                  Historial de Citas
                </h6>
              </div>
              <div className="card-body p-0">
                {detalle.citas.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <FaCalendarAlt size={32} className="mb-2 opacity-50" />
                    <p className="mb-0">Sin citas registradas</p>
                    <small>Las citas agendadas aparecerán aquí</small>
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Fecha Creación</th>
                        <th>Fecha Cita</th>
                        <th>Hora</th>
                        <th>Tipo</th>
                        <th>Estado</th>
                        <th>Observación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.citas.map(cita => (
                        <tr key={cita.id}>
                          <td>{formatFecha(cita.fechaRegistro)}</td>
                          <td>{formatFecha(cita.fecha)}</td>
                          <td>{formatHora(cita.hora)}</td>
                          <td>
                            <Badge bg={cita.tipo === 'presencial' ? 'primary' : 'info'}>
                              {cita.tipo || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={
                              cita.estado === 'pendiente' ? 'warning' :
                                cita.estado === 'confirmada' ? 'success' :
                                  cita.estado === 'realizada' ? 'primary' :
                                    cita.estado === 'cancelada' ? 'danger' : 'secondary'
                            }>
                              {cita.estado}
                            </Badge>
                          </td>
                          <td className="text-muted small">
                            {cita.observacion || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const BaseDatos = () => {
  // --- Estados Principales ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 0
  });

  // --- Estados de Filtros y Búsqueda ---
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    clasificacion: [],
  });


  const isFirstRun = React.useRef(true);
  const filterDropdownRef = React.useRef(null);
  const filterButtonRef = React.useRef(null);

  useEffect(() => {
    // Función para manejar clicks fuera del dropdown
    const handleClickOutside = (event) => {
      if (
        showFilters &&
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);

  // --- Estados de Selección y Modales ---
  const [selectedRows, setSelectedRows] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // --- Estados para Modal de Detalle ---
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // --- Estados de Upload ---
  const [uploadData, setUploadData] = useState({
    file: null,
    nombreBD: '',
    proposito: 'campania'
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [proyectosImportacion, setProyectosImportacion] = useState([]);
  const [proyectosImportacionLoading, setProyectosImportacionLoading] = useState(false);
  const [asesoresImportacion, setAsesoresImportacion] = useState([]);
  const [asesoresImportacionLoading, setAsesoresImportacionLoading] = useState(false);
  const [uploadProjectWarning, setUploadProjectWarning] = useState('');
  const [uploadAsesorWarning, setUploadAsesorWarning] = useState('');

  // --- Efectos ---
  useEffect(() => {
    loadData();
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (pagination.page === 1) loadData();
      else setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  useEffect(() => {
    if (!showUploadModal) {
      setUploadProjectWarning('');
      setUploadAsesorWarning('');
      return;
    }

    loadProyectosImportacion();
    loadAsesoresImportacion();
  }, [showUploadModal]);

  // --- Carga de Datos ---
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const backendFilters = baseDatosService.formatFiltersForBackend(filters);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...backendFilters
      };

      const response = await baseDatosService.getBaseDatos(params);
      if (response.success) {
        const formatted = baseDatosService.formatDataForFrontend(response.data.data);
        setData(formatted);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (err) {
      console.error(err);
      setError('Error cargando datos.');
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers de Filtros ---
  const handleDateChange = (date, type) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, [type]: date }
    }));
  };

  const toggleClasificacion = (value) => {
    setFilters(prev => {
      const current = prev.clasificacion;
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [value]; // Solo un filtro a la vez
      return { ...prev, clasificacion: updated };
    });
  };

  const clearFilters = () => {
    setFilters({
      dateRange: { start: null, end: null },
      clasificacion: [],
    });
  };

  // --- Handlers de Selección ---
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = data.map((_, idx) => idx);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (idx) => {
    setSelectedRows(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // --- Handler para ver detalle ---
  const handleViewDetail = (row) => {
    setSelectedLead({
      uuid: row.leadUuid,
      nombre: `${row.nombrePersona || ''} ${row.apellido || ''}`.trim() || 'Lead'
    });
    setShowDetailModal(true);
  };

  // --- Handlers de Acción (Eliminar, Exportar) ---
  const handleDelete = async () => {
    const idsToDelete = selectedRows.map(idx => data[idx]?.id).filter(Boolean);
    if (!idsToDelete.length) return;

    setLoading(true);
    try {
      await baseDatosService.deleteRecords(idsToDelete);
      toast.success('Registros eliminados correctamente');
      setSelectedRows([]);
      setShowConfirmModal(false);
      loadData();
    } catch (err) {
      toast.error('Error al eliminar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!data.length) return toast.warning('No hay datos para exportar');

    const excelRows = data.map(item => ({
      'Base Datos': item.nombre,
      'Origen': item.origen,
      'Fecha': item.fechaCreacion,
      'Nombre': item.nombrePersona,
      'Apellido': item.apellido,
      'Celular': item.celular,
      'Email': item.correo,
      'DNI': item.dni,
      'Estado': item.estadoClasificacion,
      'Citas': item.totalCitas,
      'Departamento': item.departamento
    }));

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BaseDatos");
    XLSX.writeFile(wb, `base_datos_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.get('/api/prospectos/plantilla', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', PLANTILLA_IMPORTACION_NOMBRE);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando plantilla:', err);
      toast.error('Error al descargar la plantilla.');
    }
  };

  const loadProyectosImportacion = async () => {
    setProyectosImportacionLoading(true);
    try {
      const proyectos = await getProyectosEmpresa();
      setProyectosImportacion(Array.isArray(proyectos) ? proyectos : []);
    } catch (err) {
      console.error('Error cargando proyectos para importación:', err);
      setProyectosImportacion([]);
    } finally {
      setProyectosImportacionLoading(false);
    }
  };

  const loadAsesoresImportacion = async () => {
    setAsesoresImportacionLoading(true);
    try {
      const asesores = await vendedorService.getAsesoresParaImportacion();
      setAsesoresImportacion(Array.isArray(asesores) ? asesores : []);
    } catch (err) {
      console.error('Error cargando asesores para importación:', err);
      setAsesoresImportacion([]);
    } finally {
      setAsesoresImportacionLoading(false);
    }
  };

  const getProyectoImportacionInfo = (proyecto) => {
    const id = proyecto?.codigoProyecto || proyecto?.codigo_proyecto || proyecto?.id;
    const nombre = proyecto?.nombreProyecto || proyecto?.nombre_proyecto || proyecto?.nombre || `Proyecto ${id}`;

    return { id, nombre };
  };

  const getAsesorImportacionInfo = (asesor) => {
    const nombre = [asesor?.nombre, asesor?.apellido].filter(Boolean).join(' ') || asesor?.email || 'Asesor sin nombre';

    return {
      id: asesor?.id,
      nombre,
      estado: asesor?.estado || 'activo',
    };
  };

  const renderImportProjectsTooltip = (props) => (
    <Tooltip id="import-projects-tooltip" {...props}>
      <div className="text-start small">
        <div className="fw-semibold mb-2">IDs permitidos para importación</div>
        <div className="mb-2">
          Las columnas <code>project_id</code> y <code>asesor_id</code> son obligatorias y deben usar solo IDs válidos. Si una fila trae un ID no permitido, se rechazará todo el archivo.
        </div>
        <div className="fw-semibold mt-2 mb-1">Proyectos</div>
        {proyectosImportacionLoading ? (
          <div>Cargando proyectos habilitados...</div>
        ) : proyectosImportacion.length > 0 ? (
          proyectosImportacion.map((proyecto) => {
            const { id, nombre } = getProyectoImportacionInfo(proyecto);
            return (
              <div key={id} className="d-flex justify-content-between gap-3">
                <span>{nombre}</span>
                <span className="fw-semibold">ID: {id}</span>
              </div>
            );
          })
        ) : (
          <div>No se pudo cargar la lista de proyectos habilitados.</div>
        )}
        <div className="fw-semibold mt-3 mb-1">Asesores</div>
        {asesoresImportacionLoading ? (
          <div>Cargando asesores habilitados...</div>
        ) : asesoresImportacion.length > 0 ? (
          asesoresImportacion.map((asesor) => {
            const { id, nombre, estado } = getAsesorImportacionInfo(asesor);
            return (
              <div key={id} className="d-flex justify-content-between gap-3">
                <span>{nombre}</span>
                <span className="fw-semibold">ID: {id} ({estado})</span>
              </div>
            );
          })
        ) : (
          <div>No se pudo cargar la lista de asesores.</div>
        )}
      </div>
    </Tooltip>
  );

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.nombreBD) return;

    setUploadLoading(true);
    setUploadProjectWarning('');
    setUploadAsesorWarning('');
    const formData = new FormData();
    formData.append('excel', uploadData.file);
    formData.append('nombre_bd', uploadData.nombreBD);
    formData.append('proposito_registro', uploadData.proposito);

    try {
      const response = await axios.post('/api/prospectos/procesar-excel-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const resultado = response.data?.data;
      if (resultado?.fallidos > 0) {
        const primerError = resultado?.errores?.[0]?.error || 'No se pudo completar la importación del archivo.';
        const isProjectError = primerError.toLowerCase().includes('proyecto') || primerError.toLowerCase().includes('project');
        const isAsesorError = primerError.toLowerCase().includes('asesor');

        if (isProjectError) {
          setUploadProjectWarning(ALERTA_PROYECTO_IMPORTACION);
        }

        if (isAsesorError) {
          setUploadAsesorWarning(ALERTA_ASESOR_IMPORTACION);
        }

        toast.error(primerError);
        return;
      }

      toast.success('Importación completada con éxito');
      setShowUploadModal(false);
      setUploadData({ file: null, nombreBD: '', proposito: 'campania' });
      loadData();
    } catch (err) {
      console.error(err);
      const backendMessage = err.response?.data?.message || 'Error en la importación';
      const isProjectError = backendMessage.toLowerCase().includes('proyecto') || backendMessage.toLowerCase().includes('project');
      const isAsesorError = backendMessage.toLowerCase().includes('asesor');

      if (isProjectError) {
        setUploadProjectWarning(ALERTA_PROYECTO_IMPORTACION);
      }

      if (isAsesorError) {
        setUploadAsesorWarning(ALERTA_ASESOR_IMPORTACION);
      }

      toast.error(backendMessage);
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Box>
      <div className="base-datos-container">
        {/* HEADER */}
        <div className="base-datos-header">
          <h1>Base de datos</h1>
          <button className="new-record-btn" onClick={() => setShowUploadModal(true)}>
            Nueva base de datos
          </button>
        </div>

        {/* CONTENIDO PRINCIPAL */}
        <div className="base-datos-content">
          <div className="base-datos-tools">
            {/* FILTROS TOGGLE */}
            <div className="filters-section">
              <button
                ref={filterButtonRef}
                className={`filter-btn ${showFilters || filters.clasificacion.length > 0 ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <HiAdjustments />
                Filtros
                {filters.clasificacion.length > 0 && (
                  <Badge bg="primary" className="ms-1">{filters.clasificacion.length}</Badge>
                )}
              </button>

              {showFilters && (
                <div
                  ref={filterDropdownRef}
                  className="filters-dropdown p-3 shadow-lg border rounded bg-white"
                  style={{ minWidth: '350px', position: 'absolute', zIndex: 1000, marginTop: '5px' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <h6 className="mb-0 fw-bold">Filtros de Búsqueda</h6>
                    <button
                      className="btn btn-sm btn-link text-muted p-0"
                      onClick={() => setShowFilters(false)}
                      style={{ textDecoration: 'none' }}
                    >
                      <IoClose size={20} />
                    </button>
                  </div>

                  {/* Filtro de fechas */}
                  <div className="filter-row mb-3">
                    <label className="fw-bold mb-2 small text-uppercase text-muted">Rango de Fechas</label>
                    <div className="d-flex gap-2">
                      <DatePicker
                        selected={filters.dateRange.start}
                        onChange={d => handleDateChange(d, 'start')}
                        placeholderText="Desde"
                        className="form-control form-control-sm"
                        locale="es"
                      />
                      <DatePicker
                        selected={filters.dateRange.end}
                        onChange={d => handleDateChange(d, 'end')}
                        placeholderText="Hasta"
                        className="form-control form-control-sm"
                        locale="es"
                      />
                    </div>
                  </div>

                  {/* Filtro de clasificación */}
                  <div className="filter-row mb-3">
                    <label className="fw-bold mb-2 small text-uppercase text-muted">Estado de Clasificación</label>
                    <div className="d-flex gap-2 flex-wrap">
                      {CLASIFICACION_OPTIONS.map(opt => {
                        const Icon = opt.Icon;
                        const isSelected = filters.clasificacion.includes(opt.value);
                        const colors = STATUS_COLORS[opt.value] || STATUS_COLORS.default;

                        return (
                          <div
                            key={opt.value}
                            className="py-2 px-3 cursor-pointer d-flex align-items-center gap-2 rounded-pill"
                            onClick={() => toggleClasificacion(opt.value)}
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              border: isSelected ? 'none' : '1px solid #dee2e6',
                              backgroundColor: isSelected ? colors.bg : '#f8f9fa',
                              color: isSelected ? colors.text : '#212529',
                              fontWeight: isSelected ? 600 : 400
                            }}
                          >
                            <Icon /> {opt.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Botón limpiar */}
                  <div className="text-end pt-2 border-top">
                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                      <IoClose className="me-1" /> Limpiar Todo
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* BUSCADOR */}
            <div className="search-container flex-grow-1 d-flex gap-2">
              <div className="position-relative flex-grow-1">
                <BiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, teléfono, email..."
                  className="form-control ps-4"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="excel-download-btn" onClick={handleDownloadExcel}>
                <FaDownload className="me-2" /> Excel
              </button>
            </div>
          </div>
        </div>

        {/* TABLA DE DATOS */}
        <div className="base-datos-table-wrapper mt-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p>Cargando datos...</p>
            </div>
          ) : (
            <table className="base-datos-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      onChange={e => handleSelectAll(e.target.checked)}
                      checked={data.length > 0 && selectedRows.length === data.length}
                    />
                  </th>
                  <th>Nombre BD</th>
                  <th>Origen</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Contacto</th>
                  <th>Estado</th>
                  <th>Citas</th>
                  <th style={{ width: '100px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-4 text-muted">No hay registros</td></tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={row.id || idx}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(idx)}
                          onChange={() => handleRowSelect(idx)}
                        />
                      </td>
                      <td>
                        <div className="fw-bold">{row.nombre}</div>
                        <small className="text-muted">{row.etiqueta}</small>
                      </td>
                      <td>
                        <Badge bg="light" text="dark">{row.origen}</Badge>
                      </td>
                      <td>{row.fechaCreacion}</td>
                      <td>
                        <div className="fw-semibold">{row.nombrePersona} {row.apellido}</div>
                        <small className="text-muted">{row.correo}</small>
                      </td>
                      <td>
                        <div>{row.celular}</div>
                        <small className="text-muted">{row.dni || 'Sin DNI'}</small>
                      </td>
                      <td>
                        <ClasificacionBadge clasificacion={row.estadoClasificacion} />
                        {row.totalClasificaciones > 1 && (
                          <small className="d-block text-muted mt-1">
                            +{row.totalClasificaciones - 1} anterior{row.totalClasificaciones > 2 ? 'es' : ''}
                          </small>
                        )}
                      </td>
                      <td>
                        {row.totalCitas > 0 ? (
                          <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                            <FaCalendarAlt size={10} />
                            <span>{row.totalCitas}</span>
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetail(row)}
                          disabled={!row.leadUuid}
                          title="Ver detalle completo"
                        >
                          <FaEye /> Ver
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* PAGINACIÓN */}
        <div className="d-flex justify-content-between align-items-center mt-3 p-3 bg-white rounded shadow-sm">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Mostrar:</span>
            <select
              className="form-select form-select-sm w-auto"
              value={pagination.limit}
              onChange={e => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="text-muted">
            <strong>{((pagination.page - 1) * pagination.limit) + 1}</strong> - <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> de <strong>{pagination.total}</strong>
          </div>
          <div className="d-flex gap-1">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Siguiente
            </Button>
          </div>
        </div>

        {/* BARRA FLOTANTE DE SELECCIÓN */}
        {selectedRows.length > 0 && (
          <div className="selection-alert-floating">
            <button className="close-btn" onClick={() => setSelectedRows([])}>
              <IoClose size={24} />
            </button>
            <span className="selected-count">{selectedRows.length} seleccionados</span>
            <button className="delete-btn ms-auto" onClick={() => setShowConfirmModal(true)}>
              <FaTrashAlt className="me-2" /> Eliminar
            </button>
          </div>
        )}
      </div>

      {/* MODAL DE CONFIRMACIÓN ELIMINAR */}
      <ConfirmacionModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleDelete}
        title="¿Eliminar registros?"
        message={`Se eliminarán ${selectedRows.length} registros.`}
      />

      {/* MODAL DE CARGA (UPLOAD) */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Importar Base de Datos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={uploadData.nombreBD}
                onChange={e => setUploadData({ ...uploadData, nombreBD: e.target.value })}
                placeholder="Ej: Base Noviembre 2025"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <Form.Label className="mb-0">Archivo Excel</Form.Label>
                <OverlayTrigger placement="left" trigger={['hover', 'focus']} overlay={renderImportProjectsTooltip}>
                  <button
                    type="button"
                    className="btn btn-link p-0 d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ color: '#B54708' }}
                  >
                    <FaInfoCircle />
                    <span className="small fw-semibold">IDs permitidos</span>
                  </button>
                </OverlayTrigger>
              </div>
	              <div className="mb-2">
	                <a href="#" onClick={handleDownloadTemplate} className="text-decoration-none small">
	                  <FaDownload className="me-1" /> Descargar plantilla de ejemplo
	                </a>
	              </div>
                <div className="small text-muted mb-3">
                  La plantilla requiere <code>project_id</code> y <code>asesor_id</code> para dejar asignado el proyecto y el asesor en la sesión del lead
                </div>
	              {uploadProjectWarning && (
	                <div
	                  className="small fw-semibold mb-3"
                  style={{
                    color: '#B42318',
                    background: '#FEF3F2',
                    border: '1px solid #FDA29B',
                    borderRadius: '10px',
                    padding: '10px 12px'
                  }}
                >
	                  {uploadProjectWarning}
	                </div>
	              )}
                {uploadAsesorWarning && (
                  <div
                    className="small fw-semibold mb-3"
                    style={{
                      color: '#B42318',
                      background: '#FEF3F2',
                      border: '1px solid #FDA29B',
                      borderRadius: '10px',
                      padding: '10px 12px'
                    }}
                  >
                    {uploadAsesorWarning}
                  </div>
                )}
              <div className="border border-dashed p-4 text-center rounded bg-light">
                {uploadData.file ? (
                  <div>
                    <div className="fw-bold text-success mb-2">{uploadData.file.name}</div>
                    <Button variant="outline-danger" size="sm" onClick={() => setUploadData({ ...uploadData, file: null })}>
                      Quitar
                    </Button>
                  </div>
                ) : (
                  <>
                    <FaUpload size={24} className="text-muted mb-2" />
                    <p className="small text-muted">Arrastra o selecciona un archivo .xlsx</p>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      className="form-control"
                      onChange={e => setUploadData({ ...uploadData, file: e.target.files[0] })}
                    />
                  </>
                )}
              </div>
            </Form.Group>
            <div className="d-grid">
              <Button type="submit" disabled={!uploadData.file || !uploadData.nombreBD || uploadLoading}>
                {uploadLoading ? <Spinner size="sm" /> : 'Procesar Importación'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* MODAL DE DETALLE DE LEAD */}
      <DetalleLeadModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        leadUuid={selectedLead?.uuid}
        leadNombre={selectedLead?.nombre}
      />

      <ToastContainer position="top-right" theme="colored" />
    </Box>
  );
};
